import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PersonaSetup.css';

const PersonaConversationView = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [coachNotes, setCoachNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchConversation();
  }, [conversationId]);

  const fetchConversation = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/persona/conversations/${conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversation(response.data.conversation);
      setCoachNotes(response.data.conversation.coachNotes || '');
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/persona/conversations/${conversationId}/review`,
        { coachNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotification('Marked as reviewed!');
      setTimeout(() => setNotification(''), 3000);
      fetchConversation();
    } catch (err) {
      console.error('Error marking reviewed:', err);
      setError('Failed to mark as reviewed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadTranscript = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/persona/conversations/${conversationId}/transcript`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conversation-${conversationId}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading transcript:', err);
      setError('Failed to download transcript');
    }
  };

  if (loading) {
    return (
      <div className="persona-setup-container">
        <div className="loading-spinner">Loading conversation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="persona-setup-container">
        <div className="notification error">{error}</div>
        <button onClick={() => navigate('/persona')} className="back-btn">
          Back to Persona
        </button>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="persona-setup-container">
        <p>Conversation not found</p>
        <button onClick={() => navigate('/persona')} className="back-btn">
          Back to Persona
        </button>
      </div>
    );
  }

  return (
    <div className="persona-setup-container">
      {/* Header */}
      <div className="persona-header">
        <h1>💬 Conversation Review</h1>
        <p>
          {conversation.clientId?.firstName} {conversation.clientId?.lastName} • 
          {new Date(conversation.startedAt).toLocaleDateString()}
        </p>
      </div>

      {notification && <div className="notification success">{notification}</div>}

      {/* Conversation Info */}
      <div className="form-card">
        <h2>📊 Overview</h2>
        
        <div className="conversation-meta" style={{ marginBottom: '1rem' }}>
          <span>📅 {new Date(conversation.startedAt).toLocaleString()}</span>
          <span>⏱️ {conversation.duration || 0} minutes</span>
          <span>💬 {conversation.messages?.length || 0} messages</span>
          <span className={`status-badge ${conversation.status}`}>
            {conversation.status}
          </span>
          {conversation.coachReviewed && (
            <span className="status-badge reviewed">✓ Reviewed</span>
          )}
        </div>

        {conversation.flaggedForCoach && (
          <div className="notification error" style={{ marginBottom: '1rem' }}>
            ⚠️ <strong>Flagged:</strong> {conversation.flagReason}
          </div>
        )}

        {conversation.summary && (
          <div className="conversation-summary">
            <strong>AI Summary:</strong> {conversation.summary}
          </div>
        )}

        {conversation.keyTopics?.length > 0 && (
          <div className="conversation-topics" style={{ marginTop: '1rem' }}>
            <strong>Topics: </strong>
            {conversation.keyTopics.map((topic, i) => (
              <span key={i} className="topic-tag">{topic}</span>
            ))}
          </div>
        )}

        {conversation.actionItems?.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <strong>Action Items:</strong>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              {conversation.actionItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Transcript */}
      <div className="form-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>📝 Transcript</h2>
          <button onClick={handleDownloadTranscript} className="copy-btn">
            📥 Download
          </button>
        </div>
        
        <div className="transcript-container" style={{
          maxHeight: '500px',
          overflowY: 'auto',
          background: 'rgba(247, 242, 235, 0.5)',
          borderRadius: '16px',
          padding: '1.5rem'
        }}>
          {conversation.messages
            ?.filter(m => m.role !== 'system')
            .map((message, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  background: message.role === 'client' ? 'rgba(102, 126, 234, 0.1)' : 'white',
                  borderRadius: '12px',
                  borderLeft: message.role === 'client' ? '4px solid #667eea' : '4px solid #A2D2C2'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginBottom: '0.5rem',
                  fontSize: '0.85rem',
                  color: '#5D6D7E'
                }}>
                  <strong>{message.role === 'client' ? '👤 Client' : '🤖 AI'}</strong>
                  <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                </div>
                <p style={{ margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {message.content}
                </p>
              </div>
            ))}
        </div>
      </div>

      {/* Coach Notes */}
      <div className="form-card">
        <h2>📋 Your Notes</h2>
        
        <div className="form-group">
          <textarea
            value={coachNotes}
            onChange={(e) => setCoachNotes(e.target.value)}
            placeholder="Add your notes about this conversation..."
            rows={4}
            style={{ width: '100%' }}
          />
        </div>

        {!conversation.coachReviewed && (
          <button
            onClick={handleMarkReviewed}
            className="save-btn"
            disabled={saving}
            style={{ marginTop: '1rem' }}
          >
            {saving ? 'Saving...' : '✅ Mark as Reviewed'}
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="navigation-buttons">
        <button onClick={() => navigate('/persona')}>Back to Persona</button>
        <button onClick={() => navigate(`/clientdashboard/${conversation.clientId?._id}`)}>
          View Client
        </button>
      </div>
    </div>
  );
};

export default PersonaConversationView;